import React, { useEffect, useState } from "react";
import { ClipboardList, CheckCircle2, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { Card, Pill, PrimaryButton, EmptyState } from "../UI";
import portalApi, { errMsg } from "../../lib/portalApi";
import { QUESTIONS, SCALE, INTAKE_FIELDS } from "../../lib/assessments";

const FormFill = ({ form, onDone, onBack }) => {
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const questions = QUESTIONS[form.form_type];
  const submit = async () => {
    setBusy(true);
    try {
      await portalApi.post(`/portal/forms/${form.id}/submit`, { answers });
      toast.success("Thank you — your clinician has received your answers");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card testid="portal-form-fill">
      <button onClick={onBack} className="text-sm flex items-center gap-1 mb-3" style={{ color: "var(--mf-primary)" }} data-testid="portal-form-back"><ChevronLeft className="w-4 h-4" /> Back</button>
      <h3 className="font-serif text-2xl mb-1">{form.title}</h3>
      {questions && <p className="text-sm mb-5" style={{ color: "var(--mf-text-muted)" }}>Over the last 2 weeks, how often have you been bothered by the following?</p>}
      <div className="space-y-4">
        {questions ? questions.map((q, i) => (
          <div key={i} className="p-4 rounded-xl" style={{ background: "var(--mf-muted)" }} data-testid={`portal-q${i + 1}`}>
            <div className="text-sm font-medium mb-3">{i + 1}. {q}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {SCALE.map((s) => (
                <button key={s.value} type="button" onClick={() => setAnswers({ ...answers, [`q${i + 1}`]: s.value })} className="py-2 px-2 rounded-lg text-xs transition-colors"
                  style={{ background: answers[`q${i + 1}`] === s.value ? "var(--mf-primary)" : "var(--mf-surface)", color: answers[`q${i + 1}`] === s.value ? "#FAFAF8" : "var(--mf-text)" }}
                  data-testid={`portal-q${i + 1}-a${s.value}`}>{s.label}</button>
              ))}
            </div>
          </div>
        )) : INTAKE_FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className="text-sm font-medium">{f.label}</span>
            {f.long ? <textarea rows={3} value={answers[f.key] || ""} onChange={(e) => setAnswers({ ...answers, [f.key]: e.target.value })} className="w-full mt-1 px-4 py-2.5 rounded-xl text-sm outline-none" style={{ background: "var(--mf-muted)" }} data-testid={`portal-intake-${f.key}`} />
              : <input value={answers[f.key] || ""} onChange={(e) => setAnswers({ ...answers, [f.key]: e.target.value })} className="w-full mt-1 px-4 py-2.5 rounded-xl text-sm outline-none" style={{ background: "var(--mf-muted)" }} data-testid={`portal-intake-${f.key}`} />}
          </label>
        ))}
      </div>
      {form.form_type === "phq9" && <p className="text-xs mt-4" style={{ color: "var(--mf-text-muted)" }}>If you are in crisis or thinking about harming yourself, call or text 988 (Suicide & Crisis Lifeline) or 911 now.</p>}
      <div className="mt-6"><PrimaryButton onClick={submit} disabled={busy || (questions && Object.keys(answers).length < questions.length)} testid="portal-form-submit">{busy ? "Submitting…" : "Submit"}</PrimaryButton></div>
    </Card>
  );
};

export const FormsTab = () => {
  const [forms, setForms] = useState(null);
  const [active, setActive] = useState(null);
  const load = () => portalApi.get("/portal/forms").then(({ data }) => setForms(data));
  useEffect(() => { load(); }, []);
  if (active) return <FormFill form={active} onBack={() => setActive(null)} onDone={() => { setActive(null); load(); }} />;
  return (
    <Card testid="portal-forms">
      <h3 className="font-serif text-2xl mb-4">Your forms</h3>
      {!forms ? <div className="mf-skeleton h-20" /> : forms.length === 0 ? <EmptyState icon={ClipboardList} title="Nothing to fill in" description="Your clinician will send forms here when needed." /> : (
        <div className="space-y-2">
          {forms.map((f) => (
            <div key={f.id} className="p-3 rounded-xl flex items-center gap-3" style={{ background: "var(--mf-muted)" }} data-testid={`portal-form-${f.id}`}>
              {f.status === "completed" ? <CheckCircle2 className="w-5 h-5" style={{ color: "#7BAE7F" }} /> : <ClipboardList className="w-5 h-5" style={{ color: "var(--mf-accent)" }} />}
              <span className="text-sm font-medium flex-1">{f.title}</span>
              {f.status === "pending" ? <PrimaryButton onClick={() => setActive(f)} testid={`portal-form-start-${f.id}`}>Start</PrimaryButton> : <Pill variant="success">Completed</Pill>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
